'use client'

import ConfirmModal from '@/components/confirm-modal'
import PaginationControls from '@/components/pagination/PaginationControls'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import {
    CatalogCategory,
    CatalogType,
    deleteCatalogType,
    getCatalogTypesByOrganization,
} from '@/lib/api/catalog/catalog-api'
import { useDebounce } from '@/lib/hooks/use-debounce'
import {
    useCatalogCategoryLabels,
    useServiceLabel,
} from '@/lib/hooks/use-service-label'
import { useCatalogAccess } from '@/lib/hooks/use-catalog-access'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
    MdAdd,
    MdCategory,
    MdClose,
    MdDelete,
    MdEdit,
    MdSearch,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

type CategoryTab = 'ALL' | CatalogCategory

interface TabOption {
    key: CategoryTab
    label: string
}

const CATEGORY_TAB_KEYS: CategoryTab[] = [
    'ALL',
    'CONSULTATION',
    'EXAM',
    'PROCEDURE',
    'TELECONSULTATION',
]

const CATEGORY_BADGE_CLASS: Record<CatalogCategory, string> = {
    CONSULTATION: 'badge-secondary',
    EXAM: 'badge-info',
    PROCEDURE: 'badge-secondary',
    TELECONSULTATION: 'badge-accent',
    PACKAGE: 'badge-success',
    PRODUCT: 'badge-warning',
}

export default function CatalogTypeListPage() {
    const { user, isPartner, isEmployee } = useAuth()
    const categoryLabels = useCatalogCategoryLabels()
    const hidePartnerSelector = isPartner || (isEmployee && !!user?.partnerId)
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'registrations',
        'catalog'
    )
    const [searchInput, setSearchInput] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [itemToDelete, setItemToDelete] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)
    const [partnerFilter, setPartnerFilter] = useState<number | null>(null)
    const [activeTab, setActiveTab] = useState<CategoryTab>('ALL')
    const { allowedCategories, canSeePartners } = useCatalogAccess()
    const { Consultations, Exams, Orders, Teleconsultations } =
        useServiceLabel()

    /** Abas visíveis: só as categorias cujo recurso o usuário vê no menu. */
    const categoryTabs: TabOption[] = useMemo(() => {
        const pluralLabel: Record<string, string> = {
            ALL: 'Todos',
            CONSULTATION: Consultations,
            EXAM: Exams,
            PROCEDURE: Orders,
            TELECONSULTATION: Teleconsultations,
        }
        return CATEGORY_TAB_KEYS.filter(
            key => key === 'ALL' || allowedCategories.includes(key)
        ).map(key => ({ key, label: pluralLabel[key] }))
    }, [Consultations, Exams, Orders, Teleconsultations, allowedCategories])

    const debouncedSearch = useDebounce(searchInput, 300)

    const filters = useMemo(
        () => ({
            organizationId: user?.organizationId,
            category: activeTab !== 'ALL' ? activeTab : undefined,
            partnerId: partnerFilter || undefined,
            search: debouncedSearch || undefined,
        }),
        [user?.organizationId, activeTab, partnerFilter, debouncedSearch]
    )

    const fetchFn = useCallback((params: any) => {
        if (!params.organizationId) {
            return Promise.resolve({
                success: true,
                result: [],
                total: 0,
            } as any)
        }
        return getCatalogTypesByOrganization(params.organizationId, {
            category: params.category,
            partnerId: params.partnerId,
            search: params.search,
            page: params.page,
            limit: params.limit,
        })
    }, [])

    const [urlPage, setUrlPage] = useUrlPage()

    const { items, loading, page, limit, total, setPage, reload } =
        usePaginatedPage<CatalogType, typeof filters>({
            fetchFn,
            filters,
            limit: 15,
            enabled: !!user?.organizationId,
            page: urlPage,
            onPageChange: setUrlPage,
        })

    const handleTabChange = (tab: CategoryTab) => {
        setActiveTab(tab)
    }

    const handleDelete = (id: number) => {
        setItemToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!itemToDelete) return

        setDeleting(true)
        try {
            const response = await deleteCatalogType(itemToDelete)
            if (response.success) {
                toast.success('Tipo excluído com sucesso')
                reload()
                setShowDeleteModal(false)
                setItemToDelete(null)
            } else {
                toast.error(response.error || 'Erro ao excluir tipo')
            }
        } catch (error) {
            toast.error('Erro ao excluir tipo')
        } finally {
            setDeleting(false)
        }
    }

    if (loading && items.length === 0 && total === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando tipos de catálogo...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdCategory className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Tipos de Catálogo
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {total} {total === 1 ? 'tipo' : 'tipos'} cadastrados
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={() =>
                            navigate(
                                activeTab !== 'ALL'
                                    ? `/registrations/catalog/types/novo?category=${activeTab}`
                                    : '/registrations/catalog/types/new'
                            )
                        }
                    >
                        <MdAdd className="text-xl" />
                        <span className="hidden sm:inline">Adicionar</span>
                    </button>
                )}
            </div>

            {/* Category Tabs */}
            <div className="tabs tabs-boxed bg-base-100 border border-base-300/50 p-1">
                {categoryTabs.map(tab => (
                    <button
                        key={tab.key}
                        className={`tab ${activeTab === tab.key ? 'tab-active' : ''}`}
                        onClick={() => handleTabChange(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4 flex flex-col gap-3">
                <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por nome..."
                            className="input input-bordered w-full pl-10"
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                        />
                    </div>
                    {!hidePartnerSelector && canSeePartners && (
                        <PartnerSelector
                            value={partnerFilter}
                            onChange={id => setPartnerFilter(id)}
                            label=""
                            placeholder="Parceiro"
                            className="!w-48"
                        />
                    )}
                    {searchInput && (
                        <button
                            type="button"
                            onClick={() => setSearchInput('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </div>
            </div>

            {items.length === 0 && !loading ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdCategory className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchInput
                                ? 'Nenhum tipo encontrado'
                                : 'Nenhum tipo cadastrado'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchInput
                                ? 'Tente ajustar sua busca'
                                : 'Comece adicionando tipos ao catálogo da sua empresa'}
                        </p>
                        {!searchInput && canCreate && (
                            <button
                                onClick={() =>
                                    navigate('/registrations/catalog/types/new')
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Adicionar primeiro tipo
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {items.map(item => (
                        <div
                            key={item.id}
                            className="group bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-lg transition-all duration-300"
                        >
                            <div className="flex items-center gap-4 p-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <h3 className="text-base font-semibold text-base-content">
                                            {item.name}
                                        </h3>
                                        <span
                                            className={`badge badge-sm ${CATEGORY_BADGE_CLASS[item.category]}`}
                                        >
                                            {
                                                categoryLabels[
                                                    item.category
                                                ]
                                            }
                                        </span>
                                    </div>
                                </div>

                                <div className="flex items-center gap-1">
                                    {canUpdate && (
                                        <button
                                            onClick={() =>
                                                navigate(
                                                    `/registrations/catalog/types/${item.id}`
                                                )
                                            }
                                            className="btn btn-sm btn-ghost"
                                            title="Editar"
                                        >
                                            <MdEdit className="text-lg" />
                                        </button>
                                    )}
                                    {canDelete && (
                                        <button
                                            onClick={() =>
                                                handleDelete(item.id)
                                            }
                                            className="btn btn-sm btn-ghost text-error hover:bg-error/10"
                                            title="Excluir"
                                        >
                                            <MdDelete className="text-lg" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {total > limit && (
                <PaginationControls
                    page={page}
                    limit={limit}
                    total={total}
                    loading={loading}
                    onPageChange={setPage}
                    label="tipo"
                />
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setItemToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir tipo de catálogo"
                message="Tem certeza de que deseja excluir este tipo? Itens do catálogo vinculados a este tipo perderão a associação. Esta ação não pode ser desfeita."
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
